import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import React, { useId } from "react";
import { Tooltip } from "react-tooltip";
import Badge from "../Badge/Badge";
import NotifyDot from "../NotifyDot/NotifyDot";
import useCollapsibles from "../../hooks/useCollapsibles";
import useMobile from "../../hooks/useMobile";

interface IBlocksNavigation {
  links: Array<any>;
}

const BlocksNavigation: React.FC<IBlocksNavigation> = ({ links }) => {
  const tooltipId = useId();
  const { collapsiblePreferences } = useCollapsibles();
  const { isMobile } = useMobile();
  const navigate = useNavigate();

  const filteredList = links?.filter?.(({ hide }) => !hide) || [];

  return (
    <div
      className={clsx([
        "grid",
        `grid-cols-${filteredList?.length}`,
        "md:flex",
        "md:flex-col",
        "gap-1",
        {
          "md:items-center md:gap-1": collapsiblePreferences.navIsCollapsed,
          "md:items-start md:gap-2": !collapsiblePreferences.navIsCollapsed,
        },
        "w-full",
        "md:w-auto",
        "justify-between",
        "md:justify-normal",
        "py-1",
        "px-1 md:px-0",
      ])}
    >
      {filteredList.map(
        (
          {
            link,
            icon,
            text,
            tooltip,
            onClick = () => {},
            active,
            activeClasses,
            count,
            showCount = false,
            notify = false,
            allowClickWhenActive = true,
          }: {
            link: any;
            icon: any;
            text: any;
            tooltip: any;
            active: boolean;
            onClick: any;
            activeClasses?: string;
            count?: number;
            showCount?: boolean;
            notify?: boolean;
            allowClickWhenActive: boolean;
          },
          key: number
        ) => (
          <Link
            key={key}
            to={link}
            onClick={(e: any) => {
              e?.preventDefault?.();
              if (!active || allowClickWhenActive) {
                if (onClick) {
                  onClick();
                  navigate(link);
                }
              }
            }}
            className={clsx([
              "inline-flex",
              "flex-col",
              {
                "md:flex-col md:gap-0": collapsiblePreferences.navIsCollapsed,
                "md:flex-row md:gap-3": !collapsiblePreferences.navIsCollapsed,
                "cursor-default": active && !allowClickWhenActive,
                "md:hover:dark:text-cyan-400": !active,
                "md:hover:text-red-500": !active,
                "md:py-2": collapsiblePreferences.navIsCollapsed,
                "md:py-1": !collapsiblePreferences.navIsCollapsed,
                "h-full justify-center": isMobile && !text,
              },
              "gap-2",
              "items-center",
              "md:justify-center",
              activeClasses,
              "relative",
              "transition-colors",
              "text-[11px] leading-[10px]",
              "md:text-base",
              "md:min-w-[75px]",
              "relative",
              "group",
            ])}
            data-tooltip-id={tooltipId}
            data-tooltip-content={tooltip}
            data-tooltip-place="right"
            data-tooltip-float
            data-tooltip-position-strategy="fixed"
            data-tooltip-offset={20}
          >
            {icon && (
              <span
                className={clsx([
                  "relative",
                  {
                    "text-sky-300 md:text-red-500 dark:text-cyan-400 md:dark:text-cyan-500":
                      active,
                    "group-hover:animate-pulse": !active,
                  },
                ])}
              >
                {icon}
                {!!notify && <NotifyDot />}
              </span>
            )}
            {isMobile ? (
              <>
                {!!text && (
                  <div className={clsx(["max-w-full"])}>
                    <div className="truncate">{text}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {!!(text && !collapsiblePreferences.navIsCollapsed) && (
                  <div className={clsx(["max-w-full"])}>
                    <div className="truncate">{text}</div>
                  </div>
                )}
              </>
            )}
            {showCount && !collapsiblePreferences.navIsCollapsed && (
              <Badge text={`${count}`} />
            )}
            <Tooltip id={tooltipId} />
            {showCount && collapsiblePreferences.navIsCollapsed && (
              <Badge text={`${count}`} />
            )}
          </Link>
        )
      )}
    </div>
  );
};

export default React.memo(BlocksNavigation);
